import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { InboxChatModal } from '~/features/chat/inbox-chat-modal';
import { documentToFeedItem, newChatAsset, sessionToModalAsset } from '~/features/chat/modal-asset';
import { CodingSessionModal } from '~/features/coding/coding-session-modal';
import { chatKeys } from '~/lib/query-keys';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { useCodingOpenStore } from '~/lib/stores/coding-open-store';
import { shouldCloseOnPageChange, useLegacyOpenStore } from './legacy-open-store';

/**
 * FRONT-3223: the host for chat, document and Build overlays while
 * `eddy.ui-refactoring` is OFF — what `ChatLauncher` (Home) and the coding and
 * document effects in `routes/home.tsx` did before FRONT-3146, folded into
 * one component mounted where `WindowHost` would be (`layouts/app-layout.tsx`,
 * inside `ChatProvider`). The surfaces render standalone: `WindowShell` sees
 * no window system and renders them as the full-screen overlays they were.
 *
 * Behaviour kept from before:
 * - one overlay at a time (`legacy-open-store.ts` clears the other on open);
 * - a close navigates back to the page the request came from (FRONT-1550),
 *   `replace: true` so the Home detour leaves no history entry;
 * - the "+" inside the chat window starts a fresh chat in place, keeping the
 *   origin of the window it replaces — on a chat only; a briefing item or a
 *   document opened from the feed had no "+", as on Home before;
 * - the overlays lived on Home only, so leaving for another page (Back, the
 *   sidebar, the command palette) closes them in place;
 * - the Pro Builder window opening over a chat closes it in place (inside
 *   `InboxChatModal`, which every old mount shares).
 */
export function LegacyWindowHost() {
  useConsumeOpenRequests();
  useConsumeNavigation();
  const chat = useLegacyOpenStore((s) => s.chat);
  const build = useLegacyOpenStore((s) => s.build);
  useCloseOnLeavingPage(chat !== null || build !== null);
  return (
    <>
      {chat && <LegacyChat key={chat.item.id} />}
      {build && <LegacyBuild />}
    </>
  );
}

/** The three one-shot stores' requests, consumed and cleared the instant they are read. */
function useConsumeOpenRequests(): void {
  const chatRequest = useChatOpenStore((s) => s.openRequest);
  const clearChat = useChatOpenStore((s) => s.clearOpenChat);
  useEffect(() => {
    if (!chatRequest) return;
    useLegacyOpenStore
      .getState()
      .openChat(
        sessionToModalAsset(chatRequest.sessionId, chatRequest.title, new Date().toISOString(), chatRequest.directAgent),
        chatRequest.returnTo,
      );
    clearChat();
  }, [chatRequest, clearChat]);

  const docRequest = useChatOpenStore((s) => s.openDocumentRequest);
  const clearDoc = useChatOpenStore((s) => s.clearOpenDocument);
  useEffect(() => {
    if (!docRequest) return;
    useLegacyOpenStore.getState().openChat(documentToFeedItem(docRequest.doc), docRequest.returnTo);
    clearDoc();
  }, [docRequest, clearDoc]);

  const codingRequest = useCodingOpenStore((s) => s.openRequest);
  const clearCoding = useCodingOpenStore((s) => s.clearOpenCoding);
  useEffect(() => {
    if (!codingRequest) return;
    // `fresh` (the new top bar's "always a new build") has no meaning here:
    // the old window resumes the latest build, as it always did.
    useLegacyOpenStore.getState().openBuild(codingRequest.sessionId ?? undefined, codingRequest.returnTo);
    clearCoding();
  }, [codingRequest, clearCoding]);
}

function useCloseOnLeavingPage(open: boolean): void {
  const { pathname } = useLocation();
  const previous = useRef(pathname);
  useEffect(() => {
    const from = previous.current;
    previous.current = pathname;
    if (!shouldCloseOnPageChange(open, from, pathname)) return;
    const s = useLegacyOpenStore.getState();
    s.clearChat();
    s.clearBuild();
  }, [open, pathname]);
}

/** A store function that wanted a route (Settings from a plain function) gets it here, inside the router. */
function useConsumeNavigation(): void {
  const navigate = useNavigate();
  const target = useLegacyOpenStore((s) => s.navigateTo);
  useEffect(() => {
    if (!target) return;
    useLegacyOpenStore.getState().clearNavigation();
    navigate(target);
  }, [target, navigate]);
}

function useReturnToOrigin() {
  const navigate = useNavigate();
  return useCallback(
    (returnTo: string | null) => {
      if (returnTo !== null) navigate(returnTo, { replace: true });
    },
    [navigate],
  );
}

function LegacyChat() {
  const qc = useQueryClient();
  const chat = useLegacyOpenStore((s) => s.chat);
  const returnToOrigin = useReturnToOrigin();
  const refreshThreads = useCallback(() => qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() }), [qc]);

  const closeInPlace = useCallback(() => {
    useLegacyOpenStore.getState().clearChat();
    refreshThreads();
  }, [refreshThreads]);
  const close = useCallback(() => {
    const target = useLegacyOpenStore.getState().chat?.returnTo ?? null;
    closeInPlace();
    returnToOrigin(target);
  }, [closeInPlace, returnToOrigin]);
  const startBlankChat = useCallback(() => {
    const s = useLegacyOpenStore.getState();
    s.openChat(newChatAsset(), s.chat?.returnTo ?? null);
    // The thread row is minted server-side on the first message; nudge the thread queries.
    window.setTimeout(refreshThreads, 1500);
  }, [refreshThreads]);

  if (!chat) return null;
  return <InboxChatModal item={chat.item} onClose={close} onCloseAfterNavigate={closeInPlace} onNewChat={chat.item.type === 'chat' ? startBlankChat : undefined} />;
}

function LegacyBuild() {
  const build = useLegacyOpenStore((s) => s.build);
  const returnToOrigin = useReturnToOrigin();
  const closeInPlace = useCallback(() => useLegacyOpenStore.getState().clearBuild(), []);
  const close = useCallback(() => {
    const target = useLegacyOpenStore.getState().build?.returnTo ?? null;
    closeInPlace();
    returnToOrigin(target);
  }, [closeInPlace, returnToOrigin]);

  // The old Build window closes itself in place when Pro Builder opens (as on main).

  if (!build) return null;
  return <CodingSessionModal initialSessionId={build.sessionId} onClose={close} onCloseInPlace={closeInPlace} />;
}
